import { useState } from "react";
import { Lightbulb, Loader2 } from "lucide-react";
import {
  suggestQuestionsForSource,
  type SuggestedKind,
  type SuggestedQuestion,
  type SuggestQuestionsApiError,
} from "@/lib/suggestQuestionsApi";

interface Props {
  /** Source ref the schema is fetched from; must be cognos.package.run / cognos.dataModule.run. */
  sourceRef:       { kind: string; params: Record<string, unknown> } | null;
  /** Column names currently rendered — sent so the LLM avoids suggesting trivial questions. */
  renderedColumns: string[];
  busy:            boolean;
  /** Dispatch the picked question through the narrate path — same callback the AskAboutDataInput uses. */
  onPick:          (question: string) => void;
}

/**
 * "Suggest questions" affordance — a button that, when clicked, calls
 * the LLM-backed suggest endpoint and reveals a chip row of follow-up
 * questions the user can pick from. Each chip dispatches through the
 * narrate path on click (same as typing the question into the
 * AskAboutDataInput and submitting).
 *
 * <p>Rendered only when the source ref is schema-aware (Cognos
 * package / data module). For other sources the schema endpoint can't
 * generate anything, so we hide the button rather than show a button
 * that always fails.
 *
 * <p>On-demand by design — the LLM call has a token cost, and not
 * every chart needs suggestions. The button stays visible after
 * fetching so the user can re-fetch (re-roll) if the first batch
 * isn't useful.
 */
export function SuggestedQuestions({
  sourceRef, renderedColumns, busy, onPick,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<SuggestedQuestion[]>([]);
  const [error, setError] = useState<SuggestQuestionsApiError | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);

  // Only show the button for source kinds the backend knows how to
  // fetch schema for. Anything else (uploaded files, ad-hoc SQL,
  // inline datasets, dashboards) returns an empty array — hide the
  // button rather than render a permanently-empty state. Reports
  // route via a two-hop (report → modelPath → package); see
  // VisualizeSuggestQuestionsController.SCHEMA_SOURCE_KINDS.
  const supported = sourceRef && (
    sourceRef.kind === "cognos.package.run" ||
    sourceRef.kind === "cognos.dataModule.run" ||
    sourceRef.kind === "cognos.report.run"
  );
  if (!supported || !sourceRef) return null;

  const searchPath = typeof sourceRef.params["searchPath"] === "string"
    ? (sourceRef.params["searchPath"] as string)
    : "";
  if (!searchPath) return null;

  async function fetchSuggestions() {
    if (loading || !sourceRef) return;
    setLoading(true);
    setError(null);
    setUnavailable(null);
    setOpened(true);
    try {
      const r = await suggestQuestionsForSource(
        { kind: sourceRef.kind, searchPath },
        renderedColumns,
      );
      setQuestions(r.questions);
      setUnavailable(r.questions.length === 0 ? (r.unavailableReason ?? "No suggestions available for this schema.") : null);
    } catch (e) {
      const apiErr = e as SuggestQuestionsApiError;
      setError({
        code:   apiErr?.code   ?? "unknown",
        detail: apiErr?.detail ?? "Unknown error fetching suggestions.",
        status: apiErr?.status ?? 0,
      });
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }

  // Idle state: a single pill button that opens the affordance on
  // click. Once clicked we don't go back to this state — instead the
  // button label becomes "Refresh" so the user can re-roll.
  if (!opened) {
    return (
      <button
        type="button"
        onClick={fetchSuggestions}
        disabled={busy || loading}
        aria-label="Suggest follow-up questions"
        title="Use the LLM to suggest follow-up questions based on this data's schema"
        className="mt-2 inline-flex h-7 items-center gap-1 rounded-full border border-slate-200 bg-white px-3 text-xs text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Lightbulb className="h-3 w-3" aria-hidden="true" />
        Suggest questions
      </button>
    );
  }

  return (
    <div className="mt-2 rounded-md border border-amber-100 bg-amber-50/60 p-2.5">
      <header className="mb-1.5 flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
        <span className="inline-flex items-center gap-1">
          <Lightbulb className="h-3 w-3" aria-hidden="true" />
          Suggested questions
        </span>
        <button
          type="button"
          onClick={fetchSuggestions}
          disabled={busy || loading}
          className="rounded px-1.5 py-0.5 text-[10px] font-medium text-amber-700 normal-case hover:bg-amber-100 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {loading && (
        <p className="flex items-center gap-1.5 text-[12px] text-amber-700/80">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          Analysing schema…
        </p>
      )}

      {error && !loading && (
        <p className="text-[12px] text-rose-700">
          {error.code === "llm.gateway.disabled"
            ? "LLM gateway is off — ask an admin to set DATUMCRAFT_LLM_GATEWAY_ENABLED=true."
            : error.detail}
        </p>
      )}

      {!loading && !error && questions.length === 0 && unavailable && (
        <p className="text-[12px] text-amber-700/80">{unavailable}</p>
      )}

      {!loading && questions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {questions.map((q, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPick(q.text)}
              disabled={busy}
              aria-label={`Ask: ${q.text}`}
              title={`Kind: ${q.kind}`}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] transition disabled:cursor-not-allowed disabled:opacity-50 ${kindStyle(q.kind)}`}
            >
              <span aria-hidden="true">{kindIcon(q.kind)}</span>
              {q.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Visual variant per analytical kind so the chip row reads at a
 * glance — trend chips are blue (continuity), outliers amber (alert),
 * rankings purple (ordering), etc. Same palette used in the plugin
 * for consistency.
 */
function kindStyle(kind: SuggestedKind): string {
  switch (kind) {
    case "trend":        return "bg-sky-100 text-sky-800 hover:bg-sky-200";
    case "outlier":      return "bg-amber-100 text-amber-800 hover:bg-amber-200";
    case "comparison":   return "bg-emerald-100 text-emerald-800 hover:bg-emerald-200";
    case "ranking":      return "bg-violet-100 text-violet-800 hover:bg-violet-200";
    case "distribution": return "bg-teal-100 text-teal-800 hover:bg-teal-200";
    case "relationship": return "bg-rose-100 text-rose-800 hover:bg-rose-200";
    case "summary":      return "bg-slate-200 text-slate-800 hover:bg-slate-300";
    default:             return "bg-slate-100 text-slate-700 hover:bg-slate-200";
  }
}

function kindIcon(kind: SuggestedKind): string {
  switch (kind) {
    case "trend":        return "↗";
    case "outlier":      return "⚠";
    case "comparison":   return "⇄";
    case "ranking":      return "①";
    case "distribution": return "▦";
    case "relationship": return "↔";
    case "summary":      return "Σ";
    default:             return "·";
  }
}
